/**
 * CC3Invite — partner not found / invite screen.
 * data-screen-label="cc-03 Invite · not found"
 *
 * - Auto-run toggle ON by default
 * - Copies invite URL + shows 2 s "Copied" pill
 * - "Send invite via Telegram" calls Telegram openLink
 * - POSTs /api/v1/compat-checks/invite on mount to get inviteLink
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SKU_CATALOG } from '@wds/astro-bot-shared/catalog';
import { apiFetch } from '../../../lib/api-client';
import { shareLink } from '../../../lib/share';
import { useAuth } from '../../../store/auth';
import { CCBackdrop } from './shared';
import { AppHeader } from '../../chrome/AppHeader';
import { PrimaryButton } from '../../chrome/Button';
import type { UseCompatFlowReturn } from '../../../hooks/useCompatFlow';
import styles from './Compat.module.css';

// --------------------------------------------------------------------------
// API types
// --------------------------------------------------------------------------

interface InviteResponse {
  compatCheckId: string;
  inviteLink: string;
}

// --------------------------------------------------------------------------
// Component
// --------------------------------------------------------------------------

interface CC3InviteProps {
  flow: UseCompatFlowReturn;
}

export function CC3Invite({ flow }: CC3InviteProps): JSX.Element {
  const { t } = useTranslation('flows');
  const jwt = useAuth((s) => s.jwt);
  const { searchUsername, inviteLink } = flow;

  const [autoRun, setAutoRun] = useState(true);
  const [copied, setCopied] = useState(false);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // POST invite on mount
  useEffect(() => {
    if (!jwt || !searchUsername) return;

    async function createInvite() {
      try {
        const res = await apiFetch('/api/v1/compat-checks/invite', jwt ?? '', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ invitedTelegramUsername: searchUsername }),
        });
        if (!res.ok) return;
        const data = (await res.json()) as InviteResponse;
        flow.send({ type: 'SET_INVITE_LINK', link: data.inviteLink });
        flow.send({ type: 'MARK_INVITED' });
      } catch {
        // Non-critical — invite link will just be empty
      }
    }

    void createInvite();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jwt, searchUsername]);

  function handleCopy() {
    const link = inviteLink ?? `t.me/CalesteaBot?start=cc_${searchUsername}`;
    void navigator.clipboard.writeText(link).then(() => {
      setCopied(true);
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
      copyTimerRef.current = setTimeout(() => setCopied(false), 2000);
    });
  }

  function handleSendTelegram() {
    const link = inviteLink ?? `https://t.me/CalesteaBot?start=cc_${searchUsername}`;
    // Forward the invite to a friend via Telegram's share sheet — NOT
    // openTelegramLink(link), which would open the bot chat for the sender.
    shareLink(link, t('compat.invite.body'));
  }

  const displayLink = inviteLink
    ? inviteLink
    : `t.me/CalesteaBot?start=cc_${searchUsername}`;

  return (
    <div className={styles.root} data-screen-label="cc-03 Invite · not found">
      <CCBackdrop tone="quiet" />

      <div className={styles.content}>
        {/* Nav */}
        <AppHeader title={t('flows:compat.headerTitle')} onBack={() => flow.send({ type: 'RETRY' })} headingId="compat-heading" />

        <div className={styles.inviteCenter}>
          {/* Icon */}
          <div className={styles.inviteOrb}>✦</div>

          {/* Copy text */}
          <div style={{ textAlign: 'center' }}>
            <div className={styles.eyebrow}>{t('compat.invite.eyebrow')}</div>
            <h1 className={styles.displayTitleSm}>
              @{searchUsername} {t('compat.invite.titleAfter')}
            </h1>
            <p className={styles.bodyText} style={{ textAlign: 'center', padding: '0 8px', marginTop: 10 }}>
              {t('compat.invite.body')}
            </p>
          </div>

          {/* Auto-run toggle */}
          <div className={styles.inviteToggleRow}>
            <div>
              <div className={styles.toggleLabel}>{t('compat.invite.toggleLabel')}</div>
              <div className={styles.toggleSub}>
                {t('compat.invite.toggleSub', { stars: SKU_CATALOG['compat_check'].stars })}
              </div>
            </div>
            <button
              type="button"
              className={styles.toggleSwitch}
              onClick={() => setAutoRun((v) => !v)}
              aria-pressed={autoRun}
              aria-label={t('compat.invite.toggleAria')}
            >
              <div className={[styles.toggleTrack, !autoRun ? styles.toggleTrackOff : ''].join(' ')}>
                <div className={[styles.toggleThumb, !autoRun ? styles.toggleThumbOff : ''].join(' ')} />
              </div>
            </button>
          </div>

          {/* Invite link + copy */}
          <div className={styles.inviteLinkWrap}>
            <div className={styles.inviteLinkLabel}>{t('compat.invite.linkLabel')}</div>
            <div className={styles.inviteLinkRow}>
              <span className={styles.inviteLinkText}>{displayLink}</span>
              <button
                type="button"
                className={styles.copyBtn}
                onClick={handleCopy}
                data-testid="cc3-copy-btn"
              >
                {copied ? `✓ ${t('compat.invite.copied')}` : t('compat.invite.copy')}
              </button>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className={styles.ctaArea}>
          <PrimaryButton
            onClick={handleSendTelegram}
            data-testid="cc3-send-btn"
          >
            ↗ {t('compat.invite.sendCta')}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}
